import React, { memo } from "react";

import Tag from "antd/es/tag";
import { useIntl } from "react-intl";

import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import { AvatarWithName } from "@mapmycustomers/ui";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import UserPreviewCard from "@app/component/UserPreviewCard";
import { formatRawDate } from "@app/util/formatters";

import styles from "./AdditionalDetails.module.scss";

interface Props {
  entity: Company | Deal | Person;
}

const AdditionalDetails: React.FC<Props> = ({ entity }) => {
  const intl = useIntl();

  return (
    <div className={styles.container}>
      <div className={styles.field}>
        <div>
          {intl.formatMessage({
            id: "recordPane.details.additionalDetails.createdAt",
            defaultMessage: "Created at",
            description: "Entity additional details: created at",
          })}
        </div>
        <div>
          <Tag>{formatRawDate(entity.createdAt, "PPPpp")}</Tag>
        </div>
      </div>
      {entity.creator && (
        <div className={styles.field}>
          <div>
            {intl.formatMessage({
              id: "recordPane.details.additionalDetails.createdBy",
              defaultMessage: "Created by",
              description: "Entity additional details: created by",
            })}
          </div>
          <div>
            <UserPreviewCard userId={entity.creator.id}>
              <Tag className={styles.avatar}>
                <AvatarWithName size="xs" user={entity.creator} />
              </Tag>
            </UserPreviewCard>
          </div>
        </div>
      )}
      <div className={styles.field}>
        <div>
          {intl.formatMessage({
            id: "recordPane.details.additionalDetails.updatedAt",
            defaultMessage: "Last updated",
            description: "Entity additional details: last updated",
          })}
        </div>
        <div>
          <Tag>{formatRawDate(entity.updatedAt, "PPPpp")}</Tag>
        </div>
      </div>
      {"importId" in entity && (
        <div className={styles.field}>
          <div>
            <TextWithInfo
              info={intl.formatMessage({
                id: "recordPane.details.additionalDetails.id.description",
                defaultMessage: "The unique value stored in our database to identify this record.",
                description: "Entity additional details: id description",
              })}
            >
              {intl.formatMessage({
                id: "recordPane.details.additionalDetails.id",
                defaultMessage: "Unique ID",
                description: "Entity additional details: id",
              })}
            </TextWithInfo>
          </div>
          <div>
            <Tag>{entity.importId}</Tag>
          </div>
        </div>
      )}
      {entity.crmLink && (
        <div className={styles.field}>
          <div>
            {intl.formatMessage({
              id: "recordPane.details.additionalDetails.crmLink",
              defaultMessage: "CRM Link",
              description: "Entity additional details: crm link",
            })}
          </div>
          <div className={styles.crmLink}>
            <a href={entity.crmLink} rel="noopener noreferrer" target="_blank">
              {entity.crmLink}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(AdditionalDetails);
